import { type ComponentProps, memo } from 'react';

import { type IconSize, XIcon } from '@air/design-icons';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const modalCloseButton = tailwindVariants({
  base: 'flex size-6 items-center justify-center rounded-4 border-0 bg-transparent ads:text-icon-default text-gray-11 outline-none hover:ads:bg-hover-subtle hover:bg-gray-4 focus-visible:ads:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 active:ads:bg-hover-subtle active:bg-gray-4',
  defaultVariants: {
    size: 'extra-small',
  },
  variants: {
    size: {
      'extra-small': 'size-6',
      medium: 'size-8',
      small: 'size-8',
    },
  },
});

export const modalCloseButtonIcon = tailwindVariants({
  defaultVariants: {
    size: 'extra-small',
  },
  variants: {
    size: {
      'extra-small': 'size-4',
      medium: 'size-8',
      small: 'size-6',
    },
  },
});

export type ModalCloseButtonVariants = VariantProps<typeof modalCloseButton>;

export type ModalCloseButtonProps = ComponentProps<'button'> & ModalCloseButtonVariants;

// modalCloseButtonIcon's former classes expressed as ADS size tokens: size-4 -> sm, size-6 -> lg, size-8 -> xl
const MODAL_CLOSE_BUTTON_ICON_SIZE: Record<NonNullable<ModalCloseButtonVariants['size']>, IconSize> = {
  'extra-small': 'sm',
  medium: 'xl',
  small: 'lg',
};

export const ModalCloseButton = memo(
  ({ className, size = 'extra-small', type = 'button', ...restOfProps }: ModalCloseButtonProps) => {
    return (
      <button
        className={modalCloseButton({ className, size })}
        data-testid="MODAL_CLOSE_BUTTON"
        type={type}
        {...restOfProps}
      >
        <VisuallyHidden>Close modal</VisuallyHidden>
        <XIcon size={MODAL_CLOSE_BUTTON_ICON_SIZE[size]} />
      </button>
    );
  },
);

ModalCloseButton.displayName = 'ModalCloseButton';
